import {
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  Select,
  searchQueryString,
  intoSnakeCase,
} from '../../../fetchers/_helpers';
import { CommentFields, Comment } from '../../../models/components/schemas/comment/Comment';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetAlbumVideoCommentsFields = readonly CommentFields[];

/** Options for GET /albums/{album_id}/videos/{video_id}/comments */
export type GetAlbumVideoCommentsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.comment+json */
  select: Paths;
  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /albums/{album_id}/videos/{video_id}/comments */
  query?: {
    /**
     * The comment ID to include in the paginated comments.
     */
    commentId?: number;

    /**
     * The type of comments to return.
     *
     * Option descriptions:
     *  * `non_team` - Return comments from non-team members.
     *  * `team` - Return comments from team members.
     *
     */
    commentedUserType?: `non_team` | `team`;

    /**
     * The sort direction of the results.
     *
     * Option descriptions:
     *  * `asc` - Sort the results in ascending order.
     *  * `desc` - Sort the results in descending order.
     *
     */
    direction?: `asc` | `desc`;

    /**
     * Whether to include deleted comments.
     */
    includeDeletedComments?: boolean;

    /**
     * Whether to show comments where the user is mentioned or replied to.
     */
    mentionsAndReplies?: boolean;

    /**
     * The page number of the results to show.
     */
    page?: number;

    /**
     * The number of items to show on each page of results, up to a maximum of 100.
     */
    perPage?: number;
  };
};

/** Result type for GET /albums/{album_id}/videos/{video_id}/comments */
export type GetAlbumVideoCommentsResult<Paths extends readonly string[]> = {
  /** The total number of results for GET /albums/{album_id}/videos/{video_id}/comments */
  total: number;
  /** The current page */
  page: number;
  /** The number of results per page specified in the request */
  perPage: number;
  /** Available API endpoints for traversing the paginated data */
  paging: {
    /** The next page, if available */
    next: string | null;
    /** The previous page, if available */
    previous: string | null;
    /** The first page */
    first: string;
    /** The last page */
    last: string;
  };
  /** An array containing the current results */
  data: Select<Comment, Paths>[];
};

/**
 * Get all the video comments on a video
 * This method returns every video comment on the specified video.
 */
export async function getAlbumVideoComments<Paths extends GetAlbumVideoCommentsFields>({
  baseUrl,
  select,
  where: { albumId, videoId },
  query,
  ...options
}: GetAlbumVideoCommentsOptions<Paths>): Promise<GetAlbumVideoCommentsResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/albums/${albumId}/videos/${videoId}/comments?${searchQueryString(
      query,
    )}&fields=${select.map(intoSnakeCase).join(',')}`,
    {
      ...options,
      method: 'GET',
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as GetAlbumVideoCommentsResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}

export type PostAlbumVideoCommentsFields = readonly CommentFields[];

/** Options for POST /albums/{album_id}/videos/{video_id}/comments */
export type PostAlbumVideoCommentsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.comment+json */
  select: Paths;
  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /albums/{album_id}/videos/{video_id}/comments */
  query?: {
    /**
     * The hashed password of the password-protected video.
     */
    password?: string;
  };

  /** Data serialized to body for POST /albums/{album_id}/videos/{video_id}/comments */
  variables?: {
    /**
     * The rich comment in JSON stringified form. Either this field or the **text** field is required.
     * @example {"type":"doc","content":[{"type":"paragraph","content":[{"type":"text","text":"I love this!"}]}]}
     */

    richtext?: string;

    /**
     * The comment as plain text. Either this field or the **richtext** field is required.
     * @example I love this!
     */

    text?: string;
  };
};

/** Result type for POST /albums/{album_id}/videos/{video_id}/comments */
export type PostAlbumVideoCommentsResult<Paths extends readonly string[]> = Select<Comment, Paths>;

/**
 * Add a video comment to a video
 * This method adds a video comment to the specified video.
 */
export async function postAlbumVideoComments<Paths extends PostAlbumVideoCommentsFields>({
  baseUrl,
  select,
  variables,
  where: { albumId, videoId },
  query,
  ...options
}: PostAlbumVideoCommentsOptions<Paths>): Promise<PostAlbumVideoCommentsResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/albums/${albumId}/videos/${videoId}/comments?${searchQueryString(
      query,
    )}&fields=${select.map(intoSnakeCase).join(',')}`,
    {
      ...options,
      method: 'POST',
      body: JSON.stringify(deepSnakeCase(variables)),
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as PostAlbumVideoCommentsResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}
